import env from 'js/constants/env';
/**
 * File Upload Configuration
 *
 * Environment variables:
 * - REACT_APP_FILE_UPLOAD_MAX_FILES: Maximum number of files allowed (default: 20)
 * - REACT_APP_FILE_UPLOAD_BLOCKED_EXTENSIONS: Comma-separated list of blocked/disallowed file extensions
 *   Example: ".exe,.bat,.sh,.cmd,.msi,.dll"
 *   If not set, all file types are allowed
 */

// Default maximum number of files that can be uploaded at once
export const DEFAULT_MAX_FILES = 20;

// Default blocked extensions (empty means none are blocked)
export const DEFAULT_BLOCKED_EXTENSIONS: string[] = [];

/**
 * Get maximum files limit from environment variable or default
 */
export function getMaxFilesLimit(): number {
  const envValue = env('FILE_UPLOAD_MAX_FILES');
  if (envValue) {
    const parsed = parseInt(envValue, 10);
    if (!isNaN(parsed) && parsed > 0) {
      return parsed;
    }
  }
  return DEFAULT_MAX_FILES;
}

/**
 * Get blocked file extensions from environment variable
 * Returns an array of extensions (e.g., ['.exe', '.bat', '.sh', '.mov'])
 * Returns empty array if no extensions are blocked
 */
export function getBlockedExtensions(): string[] {
  const envValue = env('FILE_UPLOAD_BLOCKED_EXTENSIONS');
  if (!envValue || envValue.trim() === '') {
    return DEFAULT_BLOCKED_EXTENSIONS;
  }

  return envValue
    .split(',')
    .map((ext) => ext.trim().toLowerCase())
    .filter((ext) => ext.length > 0)
    .map((ext) => (ext.startsWith('.') ? ext : `.${ext}`));
}

/**
 * Check if a file extension is blocked
 */
export function isExtensionBlocked(fileName: string): boolean {
  const blockedExtensions = getBlockedExtensions();
  if (blockedExtensions.length === 0) {
    return false;
  }

  const extension = fileName.includes('.') ? `.${fileName.split('.').pop()?.toLowerCase()}` : '';

  return blockedExtensions.includes(extension);
}

/**
 * Get human-readable list of blocked extensions for display
 */
export function getBlockedExtensionsDisplay(): string {
  const extensions = getBlockedExtensions();
  if (extensions.length === 0) {
    return '';
  }
  return extensions.join(', ');
}

/**
 * File upload configuration object
 */
export const fileUploadConfig = {
  get maxFiles() {
    return getMaxFilesLimit();
  },
  get blockedExtensions() {
    return getBlockedExtensions();
  },
  get blockedExtensionsDisplay() {
    return getBlockedExtensionsDisplay();
  },
  isExtensionBlocked,
};
